// PDF report card for a report spec emitted by the rhinoanalyst service's
// build_report tool. The report is laid out offscreen in the browser, captured
// with html2canvas, paginated into a jsPDF document and downloaded. Nothing is
// uploaded or stored.

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import RhinoAnalystMarkdown from './RhinoAnalystMarkdown';
import ReggieChart from './ReggieChart';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import { AVATAR_BASE_URL } from '../constants/AppConstants';

const PAGE_WIDTH_PX = 794; // A4 at 96 dpi
const PAGE_HEIGHT_PX = 1123;
const CHART_WIDTH_PX = 682; // page width minus padding

// True when every sampled pixel is (near) white. Samples a grid rather than every
// pixel: a page slice is ~1600x2250 at scale 2.
const isBlankCanvas = (c) => {
  const ctx = c.getContext('2d');
  const { width, height } = c;
  if (!width || !height) return true;
  const step = 8;
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      if (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245) return false;
    }
  }
  return true;
};

const ReportTable = ({ table }) => (
  <table className="reggie-report__table">
    <thead>
      <tr>{table.columns.map((c) => <th key={c.key}>{c.name}</th>)}</tr>
    </thead>
    <tbody>
      {table.rows.map((row, i) => (
        // Rows have no natural key; the report is static once built.
        // eslint-disable-next-line react/no-array-index-key
        <tr key={i}>
          {table.columns.map((c) => <td key={c.key}>{row[c.key] === null || row[c.key] === undefined ? '' : String(row[c.key])}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

ReportTable.propTypes = {
  table: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, name: PropTypes.string })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

const ReggieReport = ({ payload }) => {
  const {
    title, subtitle, summary, sections, filename, generated_on: generatedOn,
  } = payload;
  const organization = useSelector(getCurrentOrg);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  const logoUrl = organization?.organizationLogoUrl ? `${AVATAR_BASE_URL}${organization.organizationLogoUrl}` : null;
  const generatedLabel = moment(generatedOn || undefined).format('MMMM D, YYYY');

  const download = async () => {
    if (building || !reportRef.current) return;
    setBuilding(true);
    setError(null);
    try {
      const [{ default: html2canvas }, { jsPDF: JsPDF }] = await Promise.all([
        import(/* webpackChunkName: "reggie-pdf" */ 'html2canvas'),
        import(/* webpackChunkName: "reggie-pdf" */ 'jspdf'),
      ]);
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: PAGE_WIDTH_PX,
        windowWidth: PAGE_WIDTH_PX,
      });
      const pdf = new JsPDF({ unit: 'px', format: [PAGE_WIDTH_PX, PAGE_HEIGHT_PX], hotfixes: ['px_scaling'] });
      const pageCanvasHeight = PAGE_HEIGHT_PX * 2; // canvas is rendered at scale 2
      const sliceCount = Math.max(1, Math.ceil(canvas.height / pageCanvasHeight));
      const slices = [];
      for (let i = 0; i < sliceCount; i += 1) {
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = Math.min(pageCanvasHeight, canvas.height - i * pageCanvasHeight);
        const ctx = slice.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, i * pageCanvasHeight, canvas.width, slice.height, 0, 0, slice.width, slice.height);
        slices.push(slice);
      }
      // The capture includes the report's bottom padding, so the last slice can be a
      // blank sliver; drop trailing slices that contain no ink before paginating.
      while (slices.length > 1 && isBlankCanvas(slices[slices.length - 1])) slices.pop();
      slices.forEach((slice, i) => {
        if (i > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, PAGE_WIDTH_PX, slice.height / 2);
        pdf.setFontSize(9);
        pdf.setTextColor(120);
        pdf.text(`Page ${i + 1} of ${slices.length}`, PAGE_WIDTH_PX - 56, PAGE_HEIGHT_PX - 20, { align: 'right' });
      });
      pdf.save(filename || 'report.pdf');
    } catch (err) {
      setError('The PDF could not be built in this browser. Try again or use a different browser.');
    } finally {
      setBuilding(false);
    }
  };

  return (
    <>
      <div className="rhinoanalyst__file" data-cypress="reggieReport">
        <div className="rhinoanalyst__file__info">
          <div className="rhinoanalyst__file__name">{title}</div>
          <div className="rhinoanalyst__file__meta">
            PDF &middot; {sections.length} section{sections.length === 1 ? '' : 's'}{subtitle ? ` · ${subtitle}` : ''}
          </div>
          {error && <div className="rhinoanalyst__error u-m-t-small">{error}</div>}
        </div>
        <button type="button" className="rhinoanalyst__file__button" onClick={download} disabled={building}>
          {building ? 'Building…' : 'Download PDF'}
        </button>
      </div>

      {/* Offscreen layout captured for the PDF. aria-hidden: it duplicates the chat content. */}
      <div className="reggie-report" ref={reportRef} aria-hidden="true">
        <div className="reggie-report__header">
          <div className="reggie-report__practice">
            {logoUrl && <img className="reggie-report__logo" src={logoUrl} alt="" crossOrigin="anonymous" />}
            <div>
              <div className="reggie-report__practice__name">{organization?.name || 'Practice'}</div>
            </div>
          </div>
          <div className="reggie-report__meta">
            <div>{generatedLabel}</div>
            <div>Prepared by Reggie</div>
          </div>
        </div>

        <h1 className="reggie-report__title">{title}</h1>
        {subtitle && <div className="reggie-report__subtitle">{subtitle}</div>}
        {summary && (
          <div className="reggie-report__summary">
            <RhinoAnalystMarkdown text={summary} />
          </div>
        )}

        {sections.map((section) => (
          <div className="reggie-report__section" key={section.heading}>
            <h2 className="reggie-report__section__heading">{section.heading}</h2>
            <RhinoAnalystMarkdown text={section.analysis} />
            {section.chart && (
              <div className="reggie-report__chart">
                <ReggieChart spec={section.chart} width={CHART_WIDTH_PX} height={260} />
              </div>
            )}
            {section.table && <ReportTable table={section.table} />}
          </div>
        ))}

        <div className="reggie-report__footer">
          <span>Prepared by Reggie, the Rhinogram analytics assistant, for {organization?.name || 'this practice'}.</span>
          <span>AI insights. Verify against source reports.</span>
        </div>
      </div>
    </>
  );
};

ReggieReport.propTypes = {
  payload: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    summary: PropTypes.string,
    filename: PropTypes.string,
    generated_on: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      heading: PropTypes.string.isRequired,
      analysis: PropTypes.string,
      chart: PropTypes.object,
      table: PropTypes.object,
    })).isRequired,
  }).isRequired,
};

export default ReggieReport;
